import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { RateManagerService } from '@app/_components/rate/rate-manager';
import { AlertService } from '@app/_services';
import { ApiResponse } from '@app/_models';
import { CalendarModule } from 'primeng/calendar';
import * as moment from 'moment';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { MatDialogConfig, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RateBucketDetailFormComponent } from '../rate-bucket-detail/rate-bucket-detail-form/rate-bucket-detail-form.component';
import { RateLosComponent } from '../rate-los/rate-los.component';
import { RateSummaryService } from '.';

@Component({
    selector: 'app-rate-summary',
    templateUrl: './rate-summary.component.html',
    styleUrls: ['./rate-summary.component.css'],
    standalone: false
})
export class RateSummaryComponent implements OnInit
{
  // data
  advertisedRates: any = [];
  advertisedRateRows: any = [];
  // forms
  form1: UntypedFormGroup;
  formLoading = false;
  formAction: String;
  formLabels = [];
  formReadOnly = false;



  // to hold the current search
  currentSearchForm: any;
  // p-table
  first = 0;
  rows = 50;
  datasource;
  recordTotal: number;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];
  scrollingColumns: any[] = [];
  frozenColumns: any[] = [];
  frozenWidth: string = '400px';
  dayMs = 1000 * 3600 * 24;

  // window
  innerWidth;
  innerHeight;


  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private rateSummaryService: RateSummaryService,
    private rateManagerService: RateManagerService,
    private alertService: AlertService,
    public messageService: MessageService,
    private location: Location,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private datePipe: DatePipe) { }


  ngOnInit(): void
  {
    this.setInnerWidthHeightParameters();
  }

  getAdvertisedRatesByStrId(shortTermRentalId)
  {
    try
    {
      this.formLoading = true;
      this.rateManagerService.getAdvertisedRatesByShortTermRentalId(shortTermRentalId)
        .subscribe(
          res =>
          {
            // console.log('getAdvertisedRates SUCCESS: ' + JSON.stringify(res.data.advertisedRates));
            return res.data.advertisedRates;
          },
          err =>
          {
            console.log('getAdvertisedRates FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }



  getRatesForNewSearch(searchForm)
  {
    try
    {
      this.formLoading = true;
      this.rateSummaryService.searchRateSummaries(searchForm)
        .subscribe(
          res =>
          {
            // console.log('searchRateSummaries SUCCESS: ' + JSON.stringify(res.data.rateSummary));
            this.advertisedRates = res.data.rateSummary;
            this.buildColumns();
            this.buildRows();
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message, life: 3000 });
          },
          err =>
          {
            console.log('searchRateSummaries FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }


  getAdvertisedRatesToSpliceIntoResult(searchForm, selectedPropertyCodes: any)
  {
    try
    {
      this.formLoading = true;
      this.rateManagerService.getAdvertisedRates(searchForm)
        .subscribe(
          res =>
          {
            // console.log('getAdvertisedRates SUCCESS: ' + JSON.stringify(res.data.advertisedRates));
            let updatedAdvertisedRates = res.data.advertisedRates;
            this.spliceRows(updatedAdvertisedRates);
            this.messageService.add({ sticky: true, key: 'r', severity: 'success', summary: 'Success', detail: 'Updated the following properties with the new settings: <br><br>' + selectedPropertyCodes });
          },
          err =>
          {
            console.log('getAdvertisedRates FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }


  spliceRows(updatedAdvertisedRates)
  {

    for (let e = 0; e < updatedAdvertisedRates.length; e++)
    {
      // clear between queries
      let rateRow: any = {};

      // for each advertisedRate row in the updated advertisedRate list
      for (let index = 0; index < updatedAdvertisedRates[e].length; index++)
      {
        rateRow[index] = updatedAdvertisedRates[e][index];
      }
      rateRow.rateBucketName = updatedAdvertisedRates[e][0].rateBucketName;
      rateRow.bedrooms = updatedAdvertisedRates[e][0].bedrooms;
      rateRow.adrDevelopmentName = updatedAdvertisedRates[e][0].adrDevelopmentName;
      rateRow.propertyCode = updatedAdvertisedRates[e][0].propertyCode;

      // splice into existing array
      let itemIndex = this.advertisedRateRows.findIndex(advertisedRate => advertisedRate[0].shortTermRentalId === updatedAdvertisedRates[e][0].shortTermRentalId);
      this.advertisedRateRows[itemIndex] = rateRow;

      // use the dots so it updates when sorted https://github.com/primefaces/primeng/issues/2219
      this.advertisedRateRows = [... this.advertisedRateRows];

    }
    // console.log(this.advertisedRateRows);
    // console.log(this.advertisedRateRows.length);
  }

  buildColumns()
  {

    // clear between queries
    this.scrollingColumns = [];
    this.frozenColumns = [];

    if (this.innerWidth < 768) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' }
      ];
    } else if (this.innerWidth < 992) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Beds' }
      ];
    } else {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Beds' },
        { field: 'adrDevelopmentName', header: '' },
        { field: 'rateBucketAdjusterPct', header: 'Rate Adj' }
      ];
    }

    // unfrozen scrollingColumns - use the first property returned
    for (let index = 0; index < this.advertisedRates[0]?.length; index++)
    {

      let startDate: Date = new Date(this.advertisedRates[0][index].startDate);
      let endDate: Date = new Date(this.advertisedRates[0][index].endDate);

      this.scrollingColumns.push({
        field: index, header: new DatePipe('en-US').transform(this.advertisedRates[0][index].startDate, 'MM-dd-yy')
          + ' ' + (1 + (endDate.getTime() - startDate.getTime()) / this.dayMs) + 'd '
          + ' ' + this.advertisedRates[0][index].name, width: '70px'
      });
    }
  }

  buildRows()
  {
    // clear between queries
    this.advertisedRateRows = [];
    for (let e = 0; e < this.advertisedRates.length; e++)
    {


      // clear between queries
      let rateRow: any = {};

      // for each advertisedRate row in the advertisedRate list
      for (let index = 0; index < this.advertisedRates[e].length; index++)
      {
        rateRow[index] = this.advertisedRates[e][index];
      }
      if (this.advertisedRates[e][0]) { rateRow.rateBucketName = this.advertisedRates[e][0].rateBucketName };
      if (this.advertisedRates[e][0]) { rateRow.bedrooms = this.advertisedRates[e][0].bedrooms };
      if (this.advertisedRates[e][0]) { rateRow.adrDevelopmentName = this.advertisedRates[e][0].adrDevelopmentName };
      if (this.advertisedRates[e][0]) { rateRow.propertyCode = this.advertisedRates[e][0].propertyCode };
      if (this.advertisedRates[e][0]) { rateRow.rateBucketAdjusterPct = this.advertisedRates[e][0].rateBucketAdjusterPct };

      this.advertisedRateRows.push(rateRow);
    }
    console.log(this.advertisedRateRows);
    console.log(this.advertisedRateRows.length);
  }



  getPriceFontColor(advertisedRate: any)
  {
    if (advertisedRate)
    {
      if (advertisedRate.activeRate > advertisedRate.defaultRate)
      {
        return 'red';
      }
      if (advertisedRate.activeRate < advertisedRate.defaultRate)
      {
        return 'green';
      }
      else
      {
        return 'black';
      }
    }
  }

  getOccupiedBackgroundColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'adrDevelopmentName' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.availability === 1)
      {
        return '#ddfada';
      }
      else
      {
        return 'none';
      }
    }
  }

  getSeasonColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'adrDevelopmentName' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.rateSeasonEnd === advertisedRate.analyzedDate)
      {
        return '1px solid blue';
      }
      else
      {
        return 'none';
      }
    }
    else
    {
      return 'none';
    }
  }


  /*********
       * onOpenRateBucketDetailEdit
       *
       */
  onOpenRateBucketDetailEdit(rateBucketDetailId: number, event: Event)
  {
    // if we don't stop propagation, getSeason(), getOccupiedBackgroundColor() and getPriceFontColor() called which causes massive delay.
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      rateBucketDetailId: rateBucketDetailId,
      title: 'Edit RateBucketDetail',
      formAction: 'edit'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateBucketDetailFormComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data =>
      {
        // console.log('Dialog output:', data);

        // update the other rows with the same rateDetailId after we make an edit to anyone with that rateDetailId
        // this.updateRowsWithSameRateDetailId(rateBucketDetailId);

        // this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Updated Profile.', life: 7000 });
        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        // this.updateRateBucketDetailCB(data);

        this.messageService.add({ severity: 'success', summary: 'Updated', detail: data.message });

        // close the dialogBox
        dialogRef.close();
      });

    dialogRef.componentInstance.doDelete.subscribe(
      data =>
      {
        // console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        //  this.deleteRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });
  }


  /*********
         * Edit Button
         *
         */
  onOpenLosView(shortTermRentalId: number, analyzedDate: Date, event: Event)
  {
    // if we don't stop propagation, getSeason(), getOccupiedBackgroundColor() and getPriceFontColor() called which causes massive delay.
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    // console.log('LOS: ', analyzedDate + ' ' + shortTermRentalId);


    let endDate = new Date(analyzedDate);
    endDate.setDate(endDate.getDate() + 10);

    dialogConfig.data = {
      startDate: analyzedDate,
      endDate: endDate,
      shortTermRentalId: shortTermRentalId,
      title: 'LOS View',
      formAction: 'view',
      width: '100%',
      minWidth: '100%',
      height: '100%',
      minHeight: '100%',

    };
    // console.log('LOS: ', dialogConfig.data + ' ' + JSON.stringify(dialogConfig.data));
    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateLosComponent, dialogConfig);

  }


  updateRowsWithSameRateDetailId(rateBucketDetailId: number)
  {

    // form to submit to the server
    let searchForm: any = {};

    // list of strs we will search for to replace in current array due to update
    let selectedShortTermRentalIds = [];

    // to hold for success message
    let selectedPropertyCodes = [];

    // for each existing property in all our rates
    for (let index = 0; index < this.advertisedRates.length; index++)
    {
      const element = this.advertisedRates[index][0];

      // if the existing property has the same rateBucketDetailId as the one we just changed
      if (element.rateBucketDetailId && element.rateBucketDetailId === rateBucketDetailId)
      {
        // store the shortTermRentalId into the search form
        selectedShortTermRentalIds.push(element.shortTermRentalId);
        selectedPropertyCodes.push('<b>' + element.propertyCode + '</b><br>');
      }
    }

    // finalize the search form
    searchForm.shortTermRentalIdList = selectedShortTermRentalIds;
    searchForm.startDate = this.currentSearchForm.startDate;
    searchForm.endDate = this.currentSearchForm.endDate;
    // console.log('my search is ' + JSON.stringify(searchForm));

    this.getAdvertisedRatesToSpliceIntoResult(searchForm, selectedPropertyCodes);


  }


  onSearchSubmit(searchForm: any)
  {
    // kill the old transformation
    this.advertisedRateRows = [];
    this.frozenColumns = [];

    // store the search
    this.currentSearchForm = searchForm;
    this.getRatesForNewSearch(searchForm);
  }

  // https://stackoverflow.com/questions/59878134/primeng-table-filtering-and-sorting-on-each-column-doesnt-work
  // everytime you click in filter it just resorts.
  clickOnFilter(event: Event)
  {
    event.stopPropagation();
  }

  // when we change the rateBucketAdjusterPct
  onRateBucketAdjusterPctChange(shortTermRentalId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    let itemIndex = this.advertisedRateRows.findIndex(advertisedRate => advertisedRate[0].shortTermRentalId === shortTermRentalId);
    // Object.keys(this.advertisedRateRows[itemIndex]).forEach(e => console.log('key=' + e + ' value=' + JSON.stringify(this.advertisedRateRows[itemIndex][e])));

    Object.keys(this.advertisedRateRows[itemIndex]).forEach(e =>
    {
      if (this.advertisedRateRows[itemIndex][e] && this.advertisedRateRows[itemIndex][e].rateBucketAdjusterPct !== undefined)
      {
        this.advertisedRateRows[itemIndex][e].rateBucketAdjusterPct = newRrateBucketAdjusterPct;
      }
    });

    this.advertisedRateRows[itemIndex].rateBucketAdjusterPct = newRrateBucketAdjusterPct;
  }

  onListPriceChange(rateBucketDetailId: number, newListPrice: number)
  {

    for (let [key, value] of Object.entries(this.advertisedRateRows))
    {
      // console.log(`${key}: ${JSON.stringify(value)}`);
      Object.keys(this.advertisedRateRows[key]).forEach(e =>
      {
        if (this.advertisedRateRows[key][e] && this.advertisedRateRows[key][e].listPrice !== undefined && this.advertisedRateRows[key][e].rateBucketDetailId === rateBucketDetailId)
        {
          this.advertisedRateRows[key][e].listPrice = +newListPrice;
        }
      });
    }
  }

  incRateBucketAdjPct(shortTermRentalId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    newRrateBucketAdjusterPct = +newRrateBucketAdjusterPct;
    this.onRateBucketAdjusterPctChange(shortTermRentalId, ((!newRrateBucketAdjusterPct || newRrateBucketAdjusterPct === 0) ? 0.00 : newRrateBucketAdjusterPct) + .01, rowData);
    this.calcYearlyGrossRev(rowData);
  }

  decRateBucketAdjPct(shortTermRentalId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    newRrateBucketAdjusterPct = +newRrateBucketAdjusterPct;
    this.onRateBucketAdjusterPctChange(shortTermRentalId, ((!newRrateBucketAdjusterPct || newRrateBucketAdjusterPct === 0) ? 0.00 : newRrateBucketAdjusterPct) - .01, rowData);
    this.calcYearlyGrossRev(rowData);
  }

  incListPrice(rateBucketDetailId: number, newListPrice: number)
  {
    newListPrice = +newListPrice;
    this.onListPriceChange(rateBucketDetailId, ((!newListPrice || newListPrice === 0) ? 0.00 : newListPrice) + 10.00);
    // this.calcYearlyGrossRev(rowData);
  }

  decListPrice(rateBucketDetailId: number, newListPrice: number)
  {
    newListPrice = +newListPrice;
    this.onListPriceChange(rateBucketDetailId, ((!newListPrice || newListPrice === 0) ? 0.00 : newListPrice) - 10.00);
    // this.calcYearlyGrossRev(rowData);
  }

  formatPercent(numPercent: string)
  {
    return parseFloat(numPercent).toFixed(2);
  }

  onRateBucketAdjusterPctSave(shortTermRentalId: number, newRateBucketAdjusterPct: number)
  {
    let rateSummary: any = {};
    rateSummary.shortTermRentalId = shortTermRentalId;
    rateSummary.rateBucketAdjusterPct = newRateBucketAdjusterPct;
    console.log('saving: ' + shortTermRentalId + ' with new value ' + newRateBucketAdjusterPct + ' object ' + JSON.stringify(rateSummary));
    this.updateRateSummary(rateSummary);
  }

  onRateBucketListPriceSave(rateBucketId: number, newListPrice: number)
  {
    let rateSummary: any = {};
    rateSummary.rateBucketId = rateBucketId;
    rateSummary.listPrice = newListPrice;
    // console.log('saving: ' + rateBucketId + ' with new value ' + newListPrice + ' object ' + JSON.stringify(rateSummary));
    this.updateRateSummary(rateSummary);
  }

  updateRateSummary(rateSummary: any)
  {

    this.formLoading = false;
    this.rateSummaryService.updateRateSummary(rateSummary)
      .subscribe(
        res =>
        {
          // console.log('searchRateSummaries SUCCESS: ' + JSON.stringify(res.data.rateSummary));
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message, life: 3000 });
        },
        err =>
        {
          console.log('updateRateSummary FAIL: ' + JSON.stringify(err));
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
        },
        () => { this.formLoading = false; });
  }

  calcListPrice(advertisedRate: any)
  {
    if (advertisedRate && advertisedRate.listPrice)
    {
      return +this.formatPercent(advertisedRate.listPrice);
    }
  }

  calcPropertyListPrice(advertisedRate: any)
  {
    if (advertisedRate && advertisedRate.rateBucketAdjusterPct)
    {
      return this.calcListPrice(advertisedRate) + (this.calcListPrice(advertisedRate) * advertisedRate.rateBucketAdjusterPct)
    }
    else
    {
      return this.calcListPrice(advertisedRate);
    }
  }



  calcYMDiscFloorPrice(advertisedRate: any)
  {
    // if we have a discounting algorithm
    if (advertisedRate && advertisedRate.discountLookForwardDays && advertisedRate.discountLookForwardDays > 0 && advertisedRate.discountMaxPercent && advertisedRate.discountMaxPercent > 0)
    {
      let floor = 0.00;

      // calculate the lowest by the yield manager
      floor = +this.calcPropertyListPrice(advertisedRate) - (+this.calcPropertyListPrice(advertisedRate) * advertisedRate.discountMaxPercent);

      if (advertisedRate.minimumPriceSetByManager && advertisedRate.minimumPriceSetByManager > 0)
      {
        if (floor > advertisedRate.minimumPriceSetByManager)
        {
          return floor;
        }
        else
        {
          return advertisedRate.minimumPriceSetByManager
        }
      }
      else if (advertisedRate.minimumPriceSetByOwner && advertisedRate.minimumPriceSetByOwner > 0)
      {
        if (floor > advertisedRate.minimumPriceSetByOwner)
        {
          return floor;
        }
        else
        {
          return advertisedRate.minimumPriceSetByOwner
        }
      }
      else
      {
        return floor;
      }
    }
    else
    {
      return +this.calcPropertyListPrice(advertisedRate);
    }
  }

  calcMinListPrice(advertisedRate: any)
  {

    let response: any = {};
    let systemFloorRate = [];
    let rates = [];

    /*
    Calc the lowest allowed rate, if any set by manager, owner, or global
    */

    // calc lowest by Manager
    systemFloorRate.push(advertisedRate.minimumPriceSetByManager ? advertisedRate.minimumPriceSetByManager : 0);

    // calc lowest by Owner
    systemFloorRate.push(advertisedRate.minimumPriceSetByOwner ? advertisedRate.minimumPriceSetByOwner : 0);

    // calc lowest by global minNightly
    systemFloorRate.push(advertisedRate.minNightlyRate ? advertisedRate.minNightlyRate : 0);

    // calc lowest by rack or discounter
    let minAdRate = (this.calcPropertyListPrice(advertisedRate) -
      (+this.calcPropertyListPrice(advertisedRate) * (advertisedRate.discountMaxPercent ? advertisedRate.discountMaxPercent : 0)));


    let minSystemRate = Math.max(...systemFloorRate);


    // if we have a artificially high floor,
    if (minSystemRate > minAdRate)
    {
      response.rate = minSystemRate;
      response.color = '#FFA500';
    }
    else
    {
      response.rate = minSystemRate;
      response.color = 'transparent';
    }

    return response;

  }
  calcListPriceAdjPct(advertisedRate: any)
  {
    if (advertisedRate && advertisedRate.listPriceAdjustmentPercent)
    {
      let propertyListPrice = +this.calcPropertyListPrice(advertisedRate);
      return propertyListPrice + (propertyListPrice * advertisedRate.listPriceAdjustmentPercent)
    }
  }
  calcYearlyGrossRev(rowData: any)
  {
    let yearlyRevSummary: any = {};

    let yrListPrice = 0;
    let yrListPriceAdj = 0;
    let yrPropertyListPrice = 0;
    let yrYMDiscFloorPrice = 0;

    // if its an object of the rateBucketDetail
    if (rowData[0] && rowData[0] !== undefined && rowData[0].startDate !== undefined)
    {

      // get the date of the first rateBucketDetail
      let thisCalendarYear = new Date(rowData[0].startDate);

      Object.keys(rowData).forEach(e =>
      {
        if (rowData[e] && rowData[e].listPrice !== undefined)
        {

          let startDate: Date = new Date(rowData[e].startDate)

          if (startDate.getFullYear() === thisCalendarYear.getFullYear())
          {
            let seasonOccupancy = (+rowData[e].seasonOccupancy);
            let endDate: Date = new Date(rowData[e].endDate)

            yrListPrice = yrListPrice + (this.calcListPrice(rowData[e]) * (1 + (endDate.getTime() - startDate.getTime()) / this.dayMs) * seasonOccupancy);
            yrPropertyListPrice = yrPropertyListPrice + (this.calcPropertyListPrice(rowData[e]) * (1 + (endDate.getTime() - startDate.getTime()) / this.dayMs) * seasonOccupancy);
            yrYMDiscFloorPrice = yrYMDiscFloorPrice + (this.calcYMDiscFloorPrice(rowData[e]) * (1 + (endDate.getTime() - startDate.getTime()) / this.dayMs) * seasonOccupancy);
            yrListPriceAdj = yrListPriceAdj + (this.calcListPriceAdjPct(rowData[e]) * (1 + (endDate.getTime() - startDate.getTime()) / this.dayMs) * seasonOccupancy);
          }
        }
      });
    }
    yearlyRevSummary.yrListPrice = yrListPrice;
    yearlyRevSummary.yrListPriceAdj = yrListPriceAdj;
    yearlyRevSummary.yrPropertyListPrice = yrPropertyListPrice;
    yearlyRevSummary.yrYMDiscFloorPrice = yrYMDiscFloorPrice;

    return yearlyRevSummary;
  }

  setInnerWidthHeightParameters()
  {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight * 0.70;
    if (this.innerWidth < 768) { this.frozenWidth = '150px'; }
    else if (this.innerWidth < 992) { this.frozenWidth = '250px'; }
    else { this.frozenWidth = '400px'; }
  }
}
